'use client';

import { useEffect, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, ArrowRight, Building2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useAction } from 'next-safe-action/hooks';
import { useForm } from 'react-hook-form';
import { formatPropertyAddress } from '@addava/skills/presentation';
import { propertyDisplayName } from '@addava/domain/property-name';

import { Button } from '~/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/components/ui/form';
import { Input } from '~/components/ui/input';
import { NativeSelect, NativeSelectOption } from '~/components/ui/native-select';
import { toast } from '~/components/ui/sonner';
import { Spinner } from '~/components/ui/spinner';
import { Switch } from '~/components/ui/switch';
import { Trans } from '~/components/ui/trans';

import {
  CreatePortfolioSchema,
  RenamePortfolioSchema,
  RenamePropertySchema,
  SavePropertySchema,
  SetMonitoringSchema,
} from '~/lib/parcel-watch/schemas/parcel-watch.schema';
import {
  createPortfolioAction,
  renamePortfolioAction,
  renamePropertyAction,
  savePropertyAction,
  setMonitoringAction,
} from '~/lib/parcel-watch/server/server-actions';

import type { Portfolio, SavedProperty } from './models';
import { SignedInPropertyLookup, type SelectedAddress } from './signed-in-property-lookup';

export function CreatePortfolioForm({
  accountId,
  onSuccess,
}: {
  accountId: string;
  onSuccess: (portfolio: Portfolio) => void;
}) {
  const t = useTranslations('parcelWatch');
  const form = useForm({
    resolver: zodResolver(CreatePortfolioSchema),
    defaultValues: { accountId, name: '' },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  const action = useAction(createPortfolioAction, {
    onSuccess: ({ data }) => {
      if (data) {
        toast.success(t('portfolio.created'));
        onSuccess(data);
      }
    },
    onError: () => toast.error(t('common.actionError')),
  });
  return (
    <Form {...form}>
      <form
        className="space-y-6 pt-4"
        onSubmit={form.handleSubmit(action.execute)}
        data-test="create-portfolio-form"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <Trans i18nKey="parcelWatch.portfolio.name" />
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t('portfolio.namePlaceholder')}
                  maxLength={80}
                  data-test="portfolio-name-input"
                  autoFocus
                />
              </FormControl>
              <FormDescription>
                <Trans i18nKey="parcelWatch.portfolio.nameHelp" />
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <ActionError
          failed={Boolean(action.result.serverError || action.result.validationErrors)}
        />
        <SubmitButton
          pending={action.status === 'executing'}
          label="portfolio.create"
          test="create-portfolio-submit"
        />
      </form>
    </Form>
  );
}

export function RenamePortfolioForm({
  portfolio,
  onSuccess,
}: {
  portfolio: Portfolio;
  onSuccess: () => void;
}) {
  const t = useTranslations('parcelWatch');
  const form = useForm({
    resolver: zodResolver(RenamePortfolioSchema),
    defaultValues: {
      accountId: portfolio.account_id,
      portfolioId: portfolio.id,
      name: portfolio.name,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  const action = useAction(renamePortfolioAction, {
    onSuccess: ({ data }) => {
      if (data) {
        toast.success(t('portfolio.renamed'));
        onSuccess();
      }
    },
    onError: () => toast.error(t('common.actionError')),
  });
  return (
    <Form {...form}>
      <form
        className="space-y-6 pt-4"
        onSubmit={form.handleSubmit(action.execute)}
        data-test="rename-portfolio-form"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <Trans i18nKey="parcelWatch.portfolio.name" />
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t('portfolio.namePlaceholder')}
                  maxLength={80}
                  data-test="portfolio-rename-input"
                  autoFocus
                />
              </FormControl>
              <FormDescription>
                <Trans i18nKey="parcelWatch.portfolio.nameHelp" />
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <ActionError
          failed={Boolean(action.result.serverError || action.result.validationErrors)}
        />
        <SubmitButton
          pending={action.status === 'executing'}
          label="common.saveChanges"
          test="rename-portfolio-submit"
        />
      </form>
    </Form>
  );
}

export function RenamePropertyForm({
  property,
  onSuccess,
}: {
  property: SavedProperty;
  onSuccess: () => void;
}) {
  const t = useTranslations('parcelWatch');
  const form = useForm({
    resolver: zodResolver(RenamePropertySchema),
    defaultValues: {
      accountId: property.account_id,
      propertyId: property.id,
      nickname: property.nickname ?? '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  const action = useAction(renamePropertyAction, {
    onSuccess: ({ data }) => {
      if (data) {
        toast.success(t('property.renamed'));
        onSuccess();
      }
    },
    onError: () => toast.error(t('common.actionError')),
  });
  return (
    <Form {...form}>
      <form
        className="space-y-6 pt-4"
        onSubmit={form.handleSubmit(action.execute)}
        data-test="rename-property-form"
      >
        <FormField
          control={form.control}
          name="nickname"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <Trans i18nKey="parcelWatch.property.nickname" />
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t('property.nicknamePlaceholder')}
                  maxLength={80}
                  data-test="property-rename-input"
                  autoFocus
                />
              </FormControl>
              <FormDescription>
                <Trans i18nKey="parcelWatch.property.nicknameHelp" />
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <ActionError
          failed={Boolean(action.result.serverError || action.result.validationErrors)}
        />
        <SubmitButton
          pending={action.status === 'executing'}
          label="common.saveChanges"
          test="rename-property-submit"
        />
      </form>
    </Form>
  );
}

export function MonitoringForm({
  property,
  onSuccess,
}: {
  property: SavedProperty;
  onSuccess: () => void;
}) {
  const t = useTranslations('parcelWatch');
  const form = useForm({
    resolver: zodResolver(SetMonitoringSchema),
    defaultValues: {
      accountId: property.account_id,
      propertyId: property.id,
      monitoringEnabled: property.monitoring_enabled,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  const action = useAction(setMonitoringAction, {
    onSuccess: () => {
      toast.success(t('monitoring.saved'));
      onSuccess();
    },
    onError: () => toast.error(t('common.actionError')),
  });
  return (
    <Form {...form}>
      <form
        className="space-y-6 pt-4"
        onSubmit={form.handleSubmit(action.execute)}
        data-test="monitoring-form"
      >
        <div className="pw-sheet-property">
          <Building2 className="size-5" />
          {propertyDisplayName(property)}
        </div>
        <FormField
          control={form.control}
          name="monitoringEnabled"
          render={({ field }) => (
            <FormItem className="rounded-xl border p-5">
              <div className="flex items-center justify-between gap-4">
                <FormLabel>
                  <Trans i18nKey="parcelWatch.monitoring.enabledLabel" />
                </FormLabel>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    data-test="monitoring-toggle"
                  />
                </FormControl>
              </div>
              <FormDescription>
                <Trans i18nKey="parcelWatch.monitoring.help" />
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <p className="text-muted-foreground text-sm leading-relaxed">
          <Trans i18nKey="parcelWatch.monitoring.emailHelp" />
        </p>
        <ActionError
          failed={Boolean(action.result.serverError || action.result.validationErrors)}
        />
        <SubmitButton
          pending={action.status === 'executing'}
          label="common.saveChanges"
          test="monitoring-submit"
        />
      </form>
    </Form>
  );
}

export function SavePropertyForm(props: {
  accountId: string;
  address: SelectedAddress;
  locale: string;
  portfolios: Portfolio[];
  portfolioId?: string;
  onClose?: () => void;
  /** Save immediately: the person already asked to monitor and there is nothing to choose. */
  autoSubmit?: boolean;
}) {
  const t = useTranslations('parcelWatch');
  const autoSubmitted = useRef(false);
  const form = useForm({
    resolver: zodResolver(SavePropertySchema),
    defaultValues: {
      accountId: props.accountId,
      ...props.address,
      locale: props.locale,
      portfolioId:
        props.portfolioId ??
        props.portfolios.find((portfolio) => portfolio.is_default)?.id ??
        (props.portfolios.length === 1 ? props.portfolios[0].id : undefined),
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  const action = useAction(savePropertyAction, {
    onNavigation: () => props.onClose?.(),
    // Platform refusals such as the workspace property limit carry their own text.
    onError: ({ error }) => toast.error(error.serverError ?? t('common.actionError')),
  });
  // The ref survives Strict Mode's remount, so one sheet saves at most once.
  useEffect(() => {
    if (!props.autoSubmit || autoSubmitted.current) return;
    autoSubmitted.current = true;
    void form.handleSubmit(action.execute)();
    // Runs once, when the sheet opens with nothing left to choose.
  }, []);
  return (
    <Form {...form}>
      <form
        className="space-y-6 pt-4"
        onSubmit={form.handleSubmit(action.execute)}
        data-test="save-property-form"
      >
        {props.portfolios.length > 1 ? (
          <FormField
            control={form.control}
            name="portfolioId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey="parcelWatch.portfolio.saveIn" />
                </FormLabel>
                <FormControl>
                  <NativeSelect
                    {...field}
                    value={field.value ?? ''}
                    className="w-full"
                    data-test="save-property-portfolio"
                  >
                    {props.portfolios.map((portfolio) => (
                      <NativeSelectOption key={portfolio.id} value={portfolio.id}>
                        {portfolio.name}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                </FormControl>
                <FormDescription>
                  <Trans i18nKey="parcelWatch.portfolio.moveLater" />
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : props.portfolios.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            <Trans i18nKey="parcelWatch.portfolio.defaultHelp" />
          </p>
        ) : null}
        <p className="text-muted-foreground text-sm leading-relaxed">
          <Trans i18nKey="parcelWatch.monitoring.emailHelp" />
        </p>
        <ActionError
          failed={Boolean(action.result.serverError || action.result.validationErrors)}
          message={action.result.serverError}
        />
        <SubmitButton
          pending={action.status === 'executing' || action.status === 'hasNavigated'}
          label="property.save"
          pendingLabel="property.savingAndMonitoring"
          test="save-property-submit"
        />
      </form>
    </Form>
  );
}

export function AddPropertyForm(props: {
  initialAddress?: SelectedAddress;
  accountId: string;
  locale: string;
  portfolios: Portfolio[];
  portfolioId?: string;
  onClose: () => void;
}) {
  const [address, setAddress] = useState<SelectedAddress | null>(props.initialAddress ?? null);
  return address ? (
    <div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setAddress(null)}
        data-test="choose-another-property"
      >
        <ArrowLeft className="size-4" />
        <Trans i18nKey="parcelWatch.lookup.chooseAnother" />
      </Button>
      <div className="pw-sheet-property">
        <Building2 className="size-5" />
        <strong>{formatPropertyAddress(address.address)}</strong>
      </div>
      <SavePropertyForm {...props} address={address} />
    </div>
  ) : (
    <SignedInPropertyLookup onSelect={setAddress} />
  );
}

function ActionError({ failed, message }: { failed: boolean; message?: string | null }) {
  return failed ? (
    <p className="text-destructive text-sm" role="alert">
      {message || <Trans i18nKey="parcelWatch.common.actionError" />}
    </p>
  ) : null;
}

function SubmitButton({
  pending,
  label,
  pendingLabel = 'common.saving',
  test,
}: {
  pending: boolean;
  label: string;
  pendingLabel?: string;
  test: string;
}) {
  return (
    <Button className="h-11 w-full" disabled={pending} type="submit" data-test={test}>
      {pending ? (
        <>
          <Spinner className="size-4" />
          <Trans i18nKey={`parcelWatch.${pendingLabel}`} />
        </>
      ) : (
        <>
          <Trans i18nKey={`parcelWatch.${label}`} />
          <ArrowRight className="size-4" />
        </>
      )}
    </Button>
  );
}
